import type { PaintMode } from './types.js';
import {
  paintBar, paintCanvas, paintCtx, paintCursorSvg,
  paintCursorBg, paintCursorCircle, paintCursorFill,
  pickColorBtn, brushSizeEl, mainPanel, statusBar,
  paintColorPreview, paintModeBtn, eraseModeBtn, resetPaintBtn,
} from './dom.js';
import { paint_circle_vec, paint_line_vec, erase_circle_vec, erase_line_vec } from './pkg/svg_fit.js';

/** Whether paint mode is currently active. */
export let paintOpen: boolean = false;
/** Current paint tool: 'paint' fills, 'erase' restores original pixels. */
export let paintMode: PaintMode = 'paint';
/** Current paint color as [R, G, B] (0-255). */
export let paintColor: [number, number, number] = [255, 255, 255];
/** Whether color-picking mode is active (click to sample from image). */
export let pickingColor: boolean = false;
/** Snapshot of rgba before any painting, used for erase operations. */
export let originalRgba: Uint8ClampedArray | null = null;
/** Whether a paint/erase stroke is in progress (mouse held down). */
export let paintDragging: boolean = false;
/** Timer for debounced refit after painting. */
export let paintFitTimer: ReturnType<typeof setTimeout> | null = null;
/** Last paint cursor X in image coordinates (null when not dragging). */
export let lastPaintX: number | null = null;
/** Last paint cursor Y in image coordinates (null when not dragging). */
export let lastPaintY: number | null = null;

/** Set paint dragging state. */
export function setPaintDragging(v: boolean): void { paintDragging = v; }
/** Set last paint position in image coordinates. */
export function setLastPaint(x: number | null, y: number | null): void { lastPaintX = x; lastPaintY = y; }
/** Set the paint-fit debounce timer. */
export function setPaintFitTimer(t: ReturnType<typeof setTimeout> | null): void { paintFitTimer = t; }
/** Toggle color-picking mode. */
export function setPickingColor(v: boolean): void { pickingColor = v; }
/** Set the paint tool mode ('paint' or 'erase') and update button styles. */
export function setPaintMode(mode: PaintMode): void {
  paintMode = mode;
  paintModeBtn.className = mode === 'paint'
    ? 'flex-1 text-xs px-2 py-1 bg-amber-600 text-white rounded transition-colors'
    : 'flex-1 text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded transition-colors';
  eraseModeBtn.className = mode === 'erase'
    ? 'flex-1 text-xs px-2 py-1 bg-amber-600 text-white rounded transition-colors'
    : 'flex-1 text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded transition-colors';
}

/** Write the current rgba buffer to the main canvas via putImageData. */
export function updateMainImg(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  mainCtx: CanvasRenderingContext2D,
): void {
  mainCtx.putImageData(new ImageData(new Uint8ClampedArray(rgba), imgW, imgH), 0, 0);
}

/** Activate paint mode: show paint bar, hide cursor, snapshot rgba if needed. */
export function enterPaintMode(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
): void {
  paintOpen = true;
  mainPanel.style.cursor = 'none';
  if (!originalRgba) {
    originalRgba = new Uint8ClampedArray(rgba);
  }
  paintCanvas.width = imgW;
  paintCanvas.height = imgH;
  paintCanvas.style.display = 'block';
  paintCanvas.style.width = imgW + 'px';
  paintCanvas.style.height = imgH + 'px';
  paintCtx.imageSmoothingEnabled = false;
  statusBar.textContent = 'Paint: draw to bridge regions. Right-click to pan.';
}

/** Deactivate paint mode: hide overlays, restore cursor, cancel pending timers. */
export function exitPaintMode(): void {
  if (paintFitTimer) clearTimeout(paintFitTimer);
  paintOpen = false;
  paintCursorSvg.style.display = 'none';
  paintCanvas.style.display = 'none';
  mainPanel.style.cursor = 'crosshair';
  pickingColor = false;
  pickColorBtn.textContent = 'Pick';
}

/** Set the active paint color and update the preview swatch + cursor fill. */
export function setPaintColor(r: number, g: number, b: number): void {
  paintColor = [r, g, b];
  paintColorPreview.style.background = `rgb(${r},${g},${b})`;
  paintCursorFill.setAttribute('fill', `rgb(${r},${g},${b})`);
}

/** Reset rgba to the pre-paint snapshot, exit paint mode, redraw canvas. */
export function resetPainting(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  mainCtx: CanvasRenderingContext2D,
): void {
  if (!originalRgba) return;
  rgba.set(originalRgba);
  paintCtx.clearRect(0, 0, imgW, imgH);
  exitPaintMode();
  updateMainImg(rgba, imgW, imgH, mainCtx);
}

/** Apply a single paint/erase circle at the given image coordinates via WASM. */
export function applyPaintCircle(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  x: number, y: number,
): void {
  const radius: number = parseInt(brushSizeEl.value);
  if (paintMode === 'paint') {
    const [cr, cg, cb] = paintColor;
    paint_circle_vec(rgba, imgW, imgH, x, y, radius, cr, cg, cb);
  } else {
    erase_circle_vec(rgba, originalRgba!, imgW, imgH, x, y, radius);
  }
}

/** Apply a paint/erase line segment between two points via WASM. */
export function applyPaintLine(
  rgba: Uint8ClampedArray, imgW: number, imgH: number,
  x0: number, y0: number, x1: number, y1: number,
): void {
  const radius: number = parseInt(brushSizeEl.value);
  if (paintMode === 'paint') {
    const [cr, cg, cb] = paintColor;
    paint_line_vec(rgba, imgW, imgH, x0, y0, x1, y1, radius, cr, cg, cb);
  } else {
    erase_line_vec(rgba, originalRgba!, imgW, imgH, x0, y0, x1, y1, radius);
  }
}

/** Position and show the paint cursor SVG overlay at the given image coordinates. */
export function updatePaintCursor(imgW: number, imgH: number, x: number, y: number): void {
  paintCursorSvg.style.display = 'block';
  const r: number = parseInt(brushSizeEl.value);
  for (const el of [paintCursorBg, paintCursorCircle, paintCursorFill]) {
    el.setAttribute('cx', x.toFixed(1));
    el.setAttribute('cy', y.toFixed(1));
    el.setAttribute('r', String(r));
  }
  paintCursorSvg.setAttribute('viewBox', `0 0 ${imgW} ${imgH}`);
  paintCursorSvg.setAttribute('width', String(imgW));
  paintCursorSvg.setAttribute('height', String(imgH));
}

/** Hide the paint cursor SVG overlay. */
export function hidePaintCursor(): void {
  paintCursorSvg.style.display = 'none';
}
